import { useMemo, useState } from 'react'
import {
    MdClose,
    MdCheck,
    MdPrint,
    MdTrendingDown,
    MdExpandLess,
    MdExpandMore,
} from 'react-icons/md'
import { openPartnerComparisonPdf } from '@/lib/api/catalog/catalog-api'
import { toast } from 'react-toastify'

interface ComparisonItem {
    catalogId: number
    name: string
    quantity: number
    complimentary?: boolean
    catalog: {
        id: number
        sellingPrice?: number | null
        price?: number | null
        partners?: {
            partnerId: number
            active?: boolean
            sellingPrice?: number | null
            price?: number | null
            partner?: { id: number; name: string; tradingName?: string | null }
        }[]
    }
}

interface PartnerComparisonModalProps {
    isOpen: boolean
    onClose: () => void
    items: ComparisonItem[]
    onApplyPartner?: (partnerId: number | null) => void
    serviceTypeLabel?: string
    clientId?: number
}

const formatCents = (cents: number) =>
    `R$ ${(cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`

const NO_PARTNER_KEY = 0

export default function PartnerComparisonModal({
    isOpen,
    onClose,
    items,
    onApplyPartner,
    serviceTypeLabel,
    clientId,
}: PartnerComparisonModalProps) {
    const [printing, setPrinting] = useState(false)
    const [onlyComplete, setOnlyComplete] = useState(true)
    const [expanded, setExpanded] = useState<Record<number, boolean>>({})
    const data = useMemo(() => {
        const partnerMap = new Map<number, { id: number; name: string }>()

        const hasAnyBasePrice = items.some(item => {
            const base = item.catalog.sellingPrice || item.catalog.price || 0
            return base > 0
        })
        if (hasAnyBasePrice) {
            partnerMap.set(NO_PARTNER_KEY, {
                id: NO_PARTNER_KEY,
                name: 'Sem parceiro (valor do catálogo)',
            })
        }

        for (const item of items) {
            for (const cp of item.catalog.partners || []) {
                if (cp.active === false || !cp.partner) continue
                if (!partnerMap.has(cp.partnerId)) {
                    partnerMap.set(cp.partnerId, {
                        id: cp.partnerId,
                        name:
                            cp.partner.name ||
                            cp.partner.tradingName ||
                            `Parceiro #${cp.partnerId}`,
                    })
                }
            }
        }

        const partnersList = Array.from(partnerMap.values())

        // matrix[catalogId][partnerId] = unit price or null
        const matrix: Record<number, Record<number, number | null>> = {}
        for (const item of items) {
            matrix[item.catalogId] = {}
            const base = item.catalog.sellingPrice || item.catalog.price || 0
            matrix[item.catalogId][NO_PARTNER_KEY] = base > 0 ? base : null
            for (const cp of item.catalog.partners || []) {
                if (cp.active === false) continue
                const price = cp.sellingPrice || cp.price || 0
                matrix[item.catalogId][cp.partnerId] = price > 0 ? price : null
            }
        }

        // totals[partnerId] = { total, missing, complete }
        const totals: Record<
            number,
            { total: number; missing: number; complete: boolean }
        > = {}
        for (const p of partnersList) {
            let total = 0
            let missing = 0
            for (const item of items) {
                if (item.complimentary) continue
                const cell = matrix[item.catalogId][p.id]
                if (cell != null) {
                    total += cell * item.quantity
                } else {
                    missing++
                }
            }
            totals[p.id] = { total, missing, complete: missing === 0 }
        }

        // Lowest unit price per item across partners
        const itemMin: Record<number, number | null> = {}
        for (const item of items) {
            const vals = partnersList
                .map(p => matrix[item.catalogId][p.id])
                .filter((v): v is number => v != null)
            itemMin[item.catalogId] = vals.length > 0 ? Math.min(...vals) : null
        }

        // Best total: lowest among complete; fallback lowest overall
        const completePartners = partnersList.filter(
            p => totals[p.id].complete && totals[p.id].total > 0
        )
        const bestTotal =
            completePartners.length > 0
                ? Math.min(...completePartners.map(p => totals[p.id].total))
                : partnersList
                      .filter(p => totals[p.id].total > 0)
                      .reduce<number | null>(
                          (acc, p) =>
                              acc == null || totals[p.id].total < acc
                                  ? totals[p.id].total
                                  : acc,
                          null
                      ) || 0

        // Sort: complete first (asc by total), then incomplete (asc by total)
        const sortedPartners = [...partnersList].sort((a, b) => {
            const ta = totals[a.id]
            const tb = totals[b.id]
            if (ta.complete !== tb.complete) return ta.complete ? -1 : 1
            return ta.total - tb.total
        })

        const completeWithTotal = completePartners.map(p => totals[p.id].total)
        const maxTotal =
            completeWithTotal.length > 0 ? Math.max(...completeWithTotal) : 0
        const hasIncomplete = partnersList.some(p => !totals[p.id].complete)

        return {
            partners: sortedPartners,
            matrix,
            totals,
            itemMin,
            bestTotal,
            maxTotal,
            hasIncomplete,
            anyDataAvailable: partnersList.some(p => totals[p.id].total > 0),
        }
    }, [items])

    const visiblePartners = useMemo(
        () =>
            onlyComplete
                ? data.partners.filter(p => data.totals[p.id].complete)
                : data.partners,
        [data, onlyComplete]
    )

    if (!isOpen) return null

    const handlePrint = async () => {
        try {
            setPrinting(true)
            await openPartnerComparisonPdf({
                items: items.map(i => ({
                    catalogId: i.catalogId,
                    quantity: i.quantity,
                    complimentary: !!i.complimentary,
                })),
                serviceTypeLabel,
                clientId,
            })
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message || 'Erro ao gerar PDF da cotação'
            )
        } finally {
            setPrinting(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <MdTrendingDown className="text-secondary" />
                        Comparar parceiros
                    </h3>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost gap-1"
                            onClick={handlePrint}
                            title="Gerar PDF da cotação"
                            disabled={printing || !data.anyDataAvailable}
                        >
                            {printing ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdPrint className="text-lg" />
                            )}
                            {printing ? 'Gerando...' : 'Imprimir'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm btn-circle"
                            onClick={onClose}
                        >
                            <MdClose className="text-lg" />
                        </button>
                    </div>
                </div>

                {!data.anyDataAvailable ? (
                    <div className="py-10 text-center text-base-content/60 text-sm">
                        Nenhum parceiro com preço disponível para os itens
                        selecionados.
                    </div>
                ) : (
                    <>
                        {/* Sumário */}
                        <div className="rounded-lg bg-base-200/50 border border-base-300/50 p-3 mb-3 text-xs flex flex-wrap items-center gap-x-6 gap-y-1">
                            <div>
                                <span className="text-base-content/60">
                                    Itens:
                                </span>{' '}
                                <strong>{items.length}</strong>
                            </div>
                            <div>
                                <span className="text-base-content/60">
                                    Parceiros comparados:
                                </span>{' '}
                                <strong>{data.partners.length}</strong>
                            </div>
                            <div>
                                <span className="text-base-content/60">
                                    Menor total:
                                </span>{' '}
                                <strong className="text-success">
                                    {formatCents(data.bestTotal)}
                                </strong>
                            </div>
                            {data.hasIncomplete && (
                                <button
                                    type="button"
                                    className={`ml-auto btn btn-xs ${onlyComplete ? 'btn-outline' : 'btn-secondary'}`}
                                    onClick={() =>
                                        setOnlyComplete(prev => !prev)
                                    }
                                    title={
                                        onlyComplete
                                            ? 'Mostrar também parceiros que atendem só parte dos itens'
                                            : 'Mostrar apenas parceiros que atendem todos os itens'
                                    }
                                >
                                    {onlyComplete
                                        ? 'Mostrar parceiros parciais'
                                        : 'Somente parceiros completos'}
                                </button>
                            )}
                        </div>

                        {visiblePartners.length === 0 ? (
                            <div className="py-6 text-center text-base-content/60 text-sm">
                                Nenhum parceiro atende todos os itens
                                selecionados.
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {visiblePartners.map((p, idx) => {
                                    const tt = data.totals[p.id]
                                    const diff = tt.total - data.bestTotal
                                    const diffPercent =
                                        tt.total > 0 && diff > 0
                                            ? (diff / tt.total) * 100
                                            : 0
                                    const isFirst = idx === 0
                                    const isCheapest =
                                        tt.complete &&
                                        tt.total > 0 &&
                                        tt.total === data.bestTotal
                                    const isMostExpensive =
                                        tt.complete &&
                                        data.maxTotal > 0 &&
                                        tt.total === data.maxTotal &&
                                        data.maxTotal !== data.bestTotal
                                    const isExpanded = expanded[p.id] ?? false
                                    return (
                                        <div
                                            key={p.id}
                                            className="pcm-card rounded-lg border border-base-300/60 overflow-hidden"
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setExpanded(prev => ({
                                                        ...prev,
                                                        [p.id]: !isExpanded,
                                                    }))
                                                }
                                                className="w-full text-left bg-base-200/40 hover:bg-base-200/70 transition-colors px-4 py-2 flex items-start justify-between gap-3 flex-wrap"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-semibold leading-tight flex items-center gap-1">
                                                        {isExpanded ? (
                                                            <MdExpandLess className="text-base-content/60" />
                                                        ) : (
                                                            <MdExpandMore className="text-base-content/60" />
                                                        )}
                                                        {p.name}
                                                    </div>
                                                    {!tt.complete ? (
                                                        <div className="text-[11px] text-warning mt-0.5">
                                                            * {tt.missing}{' '}
                                                            {tt.missing === 1
                                                                ? 'item não atendido'
                                                                : 'itens não atendidos'}{' '}
                                                            — comparação parcial
                                                        </div>
                                                    ) : isFirst ? (
                                                        <div className="text-[11px] text-success mt-0.5">
                                                            Menor total
                                                            (referência)
                                                        </div>
                                                    ) : (
                                                        <div className="text-[11px] text-error mt-0.5">
                                                            +{formatCents(diff)}{' '}
                                                            · +
                                                            {diffPercent.toFixed(
                                                                1
                                                            )}
                                                            %
                                                            <span className="text-base-content/50">
                                                                {' '}
                                                                vs. menor total
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-right flex flex-col items-end gap-1">
                                                    <div className="font-bold text-base">
                                                        {formatCents(tt.total)}
                                                    </div>
                                                    <div className="flex gap-1">
                                                        {isCheapest && (
                                                            <span className="badge badge-xs badge-success gap-1">
                                                                mais barato
                                                            </span>
                                                        )}
                                                        {isMostExpensive && (
                                                            <span className="badge badge-xs badge-error gap-1">
                                                                mais caro
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>

                                            {isExpanded && (
                                                <>
                                                    <div className="divide-y divide-base-300/40">
                                                        {items.map(item => {
                                                            const cell =
                                                                data.matrix[
                                                                    item
                                                                        .catalogId
                                                                ][p.id]
                                                            // Omite itens não atendidos pra ganhar espaço.
                                                            // A flag de "X itens não atendidos" já está no header do card.
                                                            if (cell == null)
                                                                return null
                                                            const minForItem =
                                                                data.itemMin[
                                                                    item
                                                                        .catalogId
                                                                ]
                                                            const isMinForItem =
                                                                minForItem !=
                                                                    null &&
                                                                cell ===
                                                                    minForItem &&
                                                                data.partners
                                                                    .length > 1
                                                            const lineTotal =
                                                                cell *
                                                                item.quantity
                                                            return (
                                                                <div
                                                                    key={
                                                                        item.catalogId
                                                                    }
                                                                    className="px-4 py-2 flex items-baseline gap-3 text-sm"
                                                                >
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="font-medium">
                                                                            {
                                                                                item.name
                                                                            }
                                                                        </span>
                                                                        {item.quantity >
                                                                            1 && (
                                                                            <span className="text-base-content/50 text-xs ml-2">
                                                                                ×{' '}
                                                                                {
                                                                                    item.quantity
                                                                                }
                                                                            </span>
                                                                        )}
                                                                        {item.complimentary && (
                                                                            <span className="ml-2 badge badge-xs badge-ghost">
                                                                                cortesia
                                                                            </span>
                                                                        )}
                                                                        {isMinForItem &&
                                                                            !item.complimentary && (
                                                                                <span className="ml-2 badge badge-xs badge-success">
                                                                                    menor
                                                                                    preço
                                                                                </span>
                                                                            )}
                                                                    </div>
                                                                    <div className="text-right whitespace-nowrap">
                                                                        {item.complimentary ? (
                                                                            <span className="text-base-content/40">
                                                                                —
                                                                            </span>
                                                                        ) : (
                                                                            <>
                                                                                <span
                                                                                    className={
                                                                                        isMinForItem
                                                                                            ? 'font-semibold text-success'
                                                                                            : ''
                                                                                    }
                                                                                >
                                                                                    {formatCents(
                                                                                        lineTotal
                                                                                    )}
                                                                                </span>
                                                                                {item.quantity >
                                                                                    1 && (
                                                                                    <div className="text-[10px] text-base-content/40">
                                                                                        (
                                                                                        {formatCents(
                                                                                            cell
                                                                                        )}{' '}
                                                                                        cada)
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                    {(() => {
                                                        const billableItems =
                                                            items.filter(
                                                                i =>
                                                                    !i.complimentary
                                                            )
                                                        if (
                                                            tt.complete ||
                                                            billableItems.length ===
                                                                0
                                                        )
                                                            return null
                                                        const covered =
                                                            billableItems.length -
                                                            tt.missing
                                                        const ratio =
                                                            covered /
                                                            billableItems.length
                                                        if (ratio < 0.7)
                                                            return null
                                                        const missingItems =
                                                            billableItems.filter(
                                                                i =>
                                                                    data.matrix[
                                                                        i
                                                                            .catalogId
                                                                    ][p.id] ==
                                                                    null
                                                            )
                                                        return (
                                                            <div className="px-4 py-2 bg-warning/5 border-t border-base-300/40">
                                                                <div className="text-[11px] font-semibold text-warning mb-1">
                                                                    Itens não
                                                                    atendidos
                                                                    por este
                                                                    parceiro (
                                                                    {
                                                                        missingItems.length
                                                                    }{' '}
                                                                    de{' '}
                                                                    {
                                                                        billableItems.length
                                                                    }{' '}
                                                                    —{' '}
                                                                    {(
                                                                        ratio *
                                                                        100
                                                                    ).toFixed(
                                                                        0
                                                                    )}
                                                                    % de
                                                                    cobertura)
                                                                </div>
                                                                <ul className="text-xs text-base-content/70 list-disc list-inside space-y-0.5">
                                                                    {missingItems.map(
                                                                        mi => (
                                                                            <li
                                                                                key={
                                                                                    mi.catalogId
                                                                                }
                                                                            >
                                                                                {
                                                                                    mi.name
                                                                                }
                                                                            </li>
                                                                        )
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        )
                                                    })()}
                                                </>
                                            )}

                                            {onApplyPartner && (
                                                <div className="px-4 py-2 bg-base-200/30 flex justify-end pcm-no-print">
                                                    <button
                                                        type="button"
                                                        className="btn btn-xs btn-ghost gap-1"
                                                        title={`Aplicar ${p.name} como parceiro`}
                                                        onClick={() => {
                                                            onApplyPartner(
                                                                p.id ===
                                                                    NO_PARTNER_KEY
                                                                    ? null
                                                                    : p.id
                                                            )
                                                            onClose()
                                                        }}
                                                        disabled={
                                                            tt.total === 0
                                                        }
                                                    >
                                                        <MdCheck className="text-sm" />
                                                        Aplicar este parceiro
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        <p className="mt-3 text-[10px] text-base-content/50 leading-relaxed">
                            Cotação calculada com base nos preços cadastrados no
                            momento da emissão. Os valores podem sofrer
                            alteração e estão sujeitos a disponibilidade do
                            parceiro.
                        </p>
                    </>
                )}

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-sm"
                        onClick={onClose}
                    >
                        Fechar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
